import React, { useState, useEffect } from 'react';
import classes from './CreateEmployeeSalaryModal.module.css';
import api, { withCompanyId } from '../API/Api';
import Swal from 'sweetalert2';

function CreateEmployeeSalaryModal({ isOpen, onClose, onSuccess }) {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [basicSalary, setBasicSalary] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  const fetchEmployees = async () => {
    try {
      setFetchingEmployees(true);
      const response = await api.get('/v1/employee/paginated');
      // Extract employees array from paginated response: { status, message, data: { current_page, data: [...], ... } }
      const employeeList = Array.isArray(response.data?.data?.data) 
        ? response.data.data.data 
        : [];
      setEmployees(employeeList);
    } catch (error) {
      console.error('Error fetching employees:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load employees. Please try again.',
        confirmButtonColor: '#16A34A',
      });
    } finally {
      setFetchingEmployees(false);
    }
  };

  const handleEmployeeChange = (e) => {
    const employeeId = e.target.value;
    const employee = employees.find(emp => emp.id?.toString() === employeeId);
    setSelectedEmployee(employee || null);
    setBasicSalary('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEmployee) {
      Swal.fire({
        icon: 'warning',
        title: 'Required',
        text: 'Please select an employee',
        confirmButtonColor: '#16A34A',
      });
      return;
    }

    if (!basicSalary || parseFloat(basicSalary) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Required',
        text: 'Please enter a valid basic salary',
        confirmButtonColor: '#16A34A',
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        id: selectedEmployee.id,
        employee_id: selectedEmployee.employee_id || selectedEmployee.id,
        employee_name: selectedEmployee.employee_name || selectedEmployee.name,
        basic_salary: parseFloat(basicSalary),
        ...withCompanyId({}),
      };

      const response = await api.post(
        '/v1/payroll/salary_structure/create_employee_direct_salary',
        payload
      );

      if (response?.data?.status === true || response?.data?.success === true) {
        // Close modal first, then show success
        setSelectedEmployee(null);
        setBasicSalary('');
        setLoading(false);
        onClose();
        
        // Small delay to ensure modal is closed before showing alert
        setTimeout(async () => {
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: response?.data?.message || 'Employee salary created successfully',
            confirmButtonColor: '#16A34A',
          });
          
          // Trigger refresh after alert is confirmed
          if (onSuccess) {
            onSuccess();
          }
        }, 300);
      } else {
        const errorMessage = typeof response?.data?.message === 'object' 
          ? Object.values(response?.data?.message).flat().join('\n')
          : response?.data?.message || 'Failed to create employee salary';
          
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          confirmButtonColor: '#16A34A',
          zIndex: 999999,
        });
      }
    } catch (error) {
      console.error('Error creating salary:', error);
      const errorMessage = typeof error?.response?.data?.message === 'object'
        ? Object.values(error?.response?.data?.message).flat().join('\n')
        : error?.response?.data?.message || 'Something went wrong. Please try again.';
        
      await Swal.fire({
        icon: 'error',
        title: 'Server Error',
        text: errorMessage,
        confirmButtonColor: '#16A34A',
        zIndex: 999999,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.header}>
          <h2>Create Employee Basic Salary</h2>
          <button 
            className={classes.closeBtn}
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={classes.form}>
          <div className={classes.formGroup}>
            <label htmlFor="employee-select">Select Employee *</label>
            <select
              id="employee-select"
              value={selectedEmployee?.id || ''}
              onChange={handleEmployeeChange}
              disabled={fetchingEmployees || loading}
              className={classes.select}
              required
            >
              <option value="">
                {fetchingEmployees ? 'Loading employees...' : 'Choose an employee'}
              </option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.employee_name || emp.name} ({emp.employee_id || emp.id})
                </option>
              ))}
            </select>
          </div>

          {selectedEmployee && (
            <div className={classes.formGroup}>
              <label htmlFor="employee-id">Employee ID</label>
              <input
                id="employee-id"
                type="text"
                value={selectedEmployee.employee_id || selectedEmployee.id || ''}
                disabled
                className={classes.inputDisabled}
              />
            </div>
          )}

          <div className={classes.formGroup}>
            <label htmlFor="basic-salary">Basic Salary *</label>
            <input
              id="basic-salary"
              type="number"
              value={basicSalary}
              onChange={(e) => setBasicSalary(e.target.value)}
              placeholder="Enter basic salary"
              min="0"
              step="0.01"
              disabled={loading || !selectedEmployee}
              className={classes.input}
              required
            />
          </div>

          <div className={classes.actions}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`${classes.btn} ${classes.btnSecondary}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedEmployee}
              className={`${classes.btn} ${classes.btnPrimary}`}
            >
              {loading ? 'Creating...' : 'Create Salary'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEmployeeSalaryModal;
